import { useState, useEffect } from "react";
import { Link } from "wouter";
import { ChevronLeft, ChevronRight, Calendar, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEvents } from "@/events/hooks/useEvents";
import type { Event } from "@/types/schema";

// Imagem padrão para fallback
const defaultImage = "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&h=600";

interface CarouselSlide {
  id: string;
  title: string;
  description: string;
  image: string;
  date: string;
  location?: string;
}

export default function Carousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { data: eventsResponse, isLoading } = useEvents({ page: 1, limit: 5 }); // Buscar até 5 eventos para o carousel

  // Criar slides a partir dos eventos reais
  const carouselSlides: CarouselSlide[] = eventsResponse?.events?.slice(0, 3).map((event) => ({
    id: event.id,
    title: event.title,
    description: event.description || `Reviva os melhores momentos do ${event.title}. Encontre suas fotos!`,
    image: (event as any).coverimage || defaultImage, // Usar campo real coverimage
    date: event.date,
    location: event.location
  })) || [];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
  };

  const previousSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [carouselSlides.length]);

  if (isLoading) {
    return (
      <section className="carousel-container relative overflow-hidden">
        <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="text-gray-500">Carregando eventos...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="carousel-container relative overflow-hidden">
      {carouselSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`${index === currentSlide ? 'block' : 'hidden'
            } w-full h-full bg-cover bg-center relative`}
          style={{
            backgroundImage: `url(${slide.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
          data-testid={`carousel-slide-${index}`}
        >
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-lg">
              <h2 className="text-4xl font-bold mb-4">{slide.title}</h2>
              <p className="text-lg mb-4">{slide.description}</p>

              {/* Data e Local do Evento */}
              <div className="flex flex-col gap-2 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(slide.date).toLocaleDateString('pt-BR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
                {slide.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4" />
                    <span>{slide.location}</span>
                  </div>
                )}
              </div>

              <Link to={`/event/${slide.id}`}>
                <Button
                  className="bg-primary hover:bg-teal-600 text-white px-6 py-3 font-semibold transition-colors border-0 square-input"
                  data-testid={`button-carousel-${index}`}
                >
                  Ver evento
                </Button>
              </Link>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Buttons */}
      <Button
        onClick={previousSlide}
        variant="ghost"
        size="icon"
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 transition-all border-0"
        data-testid="button-carousel-previous"
      >
        <ChevronLeft className="h-6 w-6" />
      </Button>
      <Button
        onClick={nextSlide}
        variant="ghost"
        size="icon"
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 transition-all border-0"
        data-testid="button-carousel-next"
      >
        <ChevronRight className="h-6 w-6" />
      </Button>

      {/* Indicators */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {carouselSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 transition-all border-0 ${index === currentSlide
                ? 'bg-white bg-opacity-75'
                : 'bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
            data-testid={`button-carousel-indicator-${index}`}
          />
        ))}
      </div>
    </section>
  );
}
